import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { profileAPI } from '@/lib/supabase/profile';
import {
  User,
  Phone,
  Briefcase,
  MapPin,
  Calendar,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

interface ProfileSetupStepProps {
  onComplete: () => void;
  onSkip: () => void;
  onPrevious: () => void;
  onNext: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  isRequired: boolean;
  estimatedTime: string;
}

export const ProfileSetupStep: React.FC<ProfileSetupStepProps> = ({
  onComplete,
  onSkip,
  onPrevious,
  canGoBack,
  isRequired,
  estimatedTime
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    phone_number: '',
    job_title: '',
    employee_id: '',
    department: '',
    hire_date: '',
    work_phone: '',
    preferred_language: 'en',
    timezone: 'UTC'
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData['phone_number'].trim()) {
      errors['phone_number'] = 'Phone number is required';
    }

    if (!formData['job_title'].trim()) {
      errors['job_title'] = 'Job title is required';
    }

    if (!formData['employee_id'].trim()) {
      errors['employee_id'] = 'Employee ID is required';
    }

    if (!formData['department'].trim()) {
      errors['department'] = 'Department is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleComplete = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!user?.id) {
      toast.error('User not found');
      return;
    }

    try {
      setLoading(true);

      // Create or update user profile
      const profileData = {
        user_id: user.id,
        phone_number: formData.phone_number,
        job_title: formData.job_title,
        employee_id: formData.employee_id,
        department: formData.department,
        hire_date: formData.hire_date || null,
        work_phone: formData.work_phone || null,
        preferred_language: formData.preferred_language,
        timezone: formData.timezone,
        onboarding_step: 'permissions_review'
      };

      const existingProfile = await profileAPI.getUserProfile(user.id);
      let result;

      if (existingProfile) {
        result = await profileAPI.updateUserProfile(user.id, profileData);
      } else {
        result = await profileAPI.createUserProfile(profileData);
      }

      if (result) {
        // Calculate profile completion
        const completionPercentage = await profileAPI.calculateProfileCompletion(user.id);
        await profileAPI.updateUserProfile(user.id, {
          profile_completion_percentage: completionPercentage
        });

        toast.success('Profile setup completed!');
        onComplete();
      } else {
        toast.error('Failed to save profile');
      }
    } catch (error) {
      toast.error('Failed to complete profile setup');
    } finally {
      setLoading(false);
    }
  };

  const getCompletionPercentage = () => {
    const requiredFields = ['phone_number', 'job_title', 'employee_id', 'department'];
    const completedFields = requiredFields.filter(field =>
      formData[field as keyof typeof formData].trim()
    );
    return Math.round((completedFields.length / requiredFields.length) * 100);
  };

  const completionPercentage = getCompletionPercentage();

  return (
    <div className='space-y-6'>
      {/* Progress Indicator */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div className='w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center'>
            <span className='text-sm font-medium text-primary'>{completionPercentage}%</span>
          </div>
          <span className='text-sm text-muted-foreground'>Profile completion</span>
        </div>
        <Badge variant='outline'>{estimatedTime}</Badge>
      </div>

      {/* Form */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2 text-lg'>
              <User className='h-5 w-5' />
              Basic Information
            </CardTitle>
            <CardDescription>Your essential contact and work details</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <Label htmlFor='phone_number' className='flex items-center gap-2'>
                <Phone className='h-4 w-4' />
                Phone Number *
              </Label>
              <Input
                id='phone_number'
                value={formData.phone_number}
                onChange={e => handleInputChange('phone_number', e.target.value)}
                placeholder='+1 (555) 123-4567'
                className={validationErrors['phone_number'] ? 'border-red-500' : ''}
              />
              {validationErrors['phone_number'] && (
                <p className='text-sm text-red-500 mt-1'>{validationErrors['phone_number']}</p>
              )}
            </div>

            <div>
              <Label htmlFor='job_title' className='flex items-center gap-2'>
                <Briefcase className='h-4 w-4' />
                Job Title *
              </Label>
              <Input
                id='job_title'
                value={formData.job_title}
                onChange={e => handleInputChange('job_title', e.target.value)}
                placeholder='Service Technician'
                className={validationErrors['job_title'] ? 'border-red-500' : ''}
              />
              {validationErrors['job_title'] && (
                <p className='text-sm text-red-500 mt-1'>{validationErrors['job_title']}</p>
              )}
            </div>

            <div>
              <Label htmlFor='employee_id' className='flex items-center gap-2'>
                <User className='h-4 w-4' />
                Employee ID *
              </Label>
              <Input
                id='employee_id'
                value={formData.employee_id}
                onChange={e => handleInputChange('employee_id', e.target.value)}
                placeholder='EMP001'
                className={validationErrors['employee_id'] ? 'border-red-500' : ''}
              />
              {validationErrors['employee_id'] && (
                <p className='text-sm text-red-500 mt-1'>{validationErrors['employee_id']}</p>
              )}
            </div>

            <div>
              <Label htmlFor='department' className='flex items-center gap-2'>
                <MapPin className='h-4 w-4' />
                Department *
              </Label>
              <Select
                value={formData.department}
                onValueChange={value => handleInputChange('department', value)}
              >
                <SelectTrigger className={validationErrors['department'] ? 'border-red-500' : ''}>
                  <SelectValue placeholder='Select department' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='service'>Service Department</SelectItem>
                  <SelectItem value='parts'>Parts Department</SelectItem>
                  <SelectItem value='sales'>Sales Department</SelectItem>
                  <SelectItem value='management'>Management</SelectItem>
                  <SelectItem value='admin'>Administration</SelectItem>
                  <SelectItem value='other'>Other</SelectItem>
                </SelectContent>
              </Select>
              {validationErrors['department'] && (
                <p className='text-sm text-red-500 mt-1'>{validationErrors['department']}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2 text-lg'>
              <Calendar className='h-5 w-5' />
              Additional Information
            </CardTitle>
            <CardDescription>Optional details to complete your profile</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <Label htmlFor='hire_date'>Hire Date</Label>
              <Input
                id='hire_date'
                type='date'
                value={formData.hire_date}
                onChange={e => handleInputChange('hire_date', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor='work_phone'>Work Phone</Label>
              <Input
                id='work_phone'
                value={formData.work_phone}
                onChange={e => handleInputChange('work_phone', e.target.value)}
                placeholder='+1 (555) 123-4567'
              />
            </div>

            <div>
              <Label htmlFor='preferred_language'>Preferred Language</Label>
              <Select
                value={formData.preferred_language}
                onValueChange={value => handleInputChange('preferred_language', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='en'>English</SelectItem>
                  <SelectItem value='es'>Spanish</SelectItem>
                  <SelectItem value='fr'>French</SelectItem>
                  <SelectItem value='de'>German</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor='timezone'>Timezone</Label>
              <Select
                value={formData.timezone}
                onValueChange={value => handleInputChange('timezone', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='UTC'>UTC</SelectItem>
                  <SelectItem value='America/New_York'>Eastern Time</SelectItem>
                  <SelectItem value='America/Chicago'>Central Time</SelectItem>
                  <SelectItem value='America/Denver'>Mountain Time</SelectItem>
                  <SelectItem value='America/Los_Angeles'>Pacific Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Completion Status */}
      <div className='flex items-center gap-2 p-4 bg-muted/50 rounded-lg'>
        {completionPercentage === 100 ? (
          <>
            <CheckCircle className='h-5 w-5 text-green-600' />
            <span className='text-sm text-green-600 font-medium'>
              All required fields completed! You can proceed to the next step.
            </span>
          </>
        ) : (
          <>
            <AlertCircle className='h-5 w-5 text-orange-600' />
            <span className='text-sm text-orange-600'>
              Please complete all required fields (marked with *) to continue.
            </span>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-6 border-t'>
        <div className='flex gap-2'>
          {canGoBack && (
            <Button variant='outline' onClick={onPrevious}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Previous
            </Button>
          )}
        </div>

        <div className='flex gap-2'>
          {!isRequired && (
            <Button variant='ghost' onClick={onSkip}>
              Skip for now
            </Button>
          )}
          <Button onClick={handleComplete} disabled={loading || completionPercentage < 100}>
            {loading ? (
              <>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                Saving...
              </>
            ) : (
              <>
                Complete Setup
                <ArrowRight className='h-4 w-4 ml-2' />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
